import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Code, Zap, Clock, Globe, Brain, Shield,
  TrendingUp, Settings, CheckCircle2, Cpu, Layers
} from 'lucide-react';

const CustomAIDevelopment = () => {
  const whatWeBuild = [
    {
      icon: Brain,
      title: 'AI-Powered SaaS Products',
      description: 'Full-stack AI products from idea to launch — LLM backends, vector search, multi-tenant architecture, billing',
      examples: ['Document intelligence platforms', 'AI writing assistants', 'Automated research tools', 'Personalisation engines']
    },
    {
      icon: Cpu,
      title: 'Predictive ML Models',
      description: 'Custom machine learning models trained on your data — churn prediction, demand forecasting, fraud detection',
      examples: ['Churn & LTV prediction', 'Demand forecasting', 'Fraud & anomaly detection', 'Price optimisation']
    },
    {
      icon: Settings,
      title: 'AI Process Automation',
      description: 'End-to-end workflow automation powered by AI — document processing, data extraction, decision engines',
      examples: ['Invoice & contract extraction', 'Automated compliance checks', 'Intelligent routing systems', 'AI approval workflows']
    },
    {
      icon: Globe,
      title: 'Computer Vision Systems',
      description: 'Image and video AI — quality inspection, object detection, OCR, and visual analytics at scale',
      examples: ['Manufacturing quality control', 'Retail shelf analysis', 'Medical image analysis', 'Document scanning & OCR']
    },
    {
      icon: Layers,
      title: 'AI Data Platforms',
      description: 'Build the data infrastructure that powers your AI — feature stores, ML pipelines, real-time serving',
      examples: ['Feature engineering pipelines', 'Model serving APIs', 'Real-time prediction endpoints', 'A/B testing infrastructure']
    },
    {
      icon: Code,
      title: 'AI Integration & APIs',
      description: 'Embed AI capabilities into your existing products via clean, well-documented APIs and SDKs',
      examples: ['REST & GraphQL AI APIs', 'Webhook-driven AI pipelines', 'SDK development', 'AI microservices']
    }
  ];

  const caseStudies = [
    {
      industry: 'Healthcare',
      challenge: 'Manual patient risk stratification taking 2 hours per clinician per day — delays in intervention',
      solution: 'Custom ML model using 50+ clinical features. Built on AWS SageMaker, integrated with EHR via HL7 FHIR.',
      result: '40% improvement in early risk detection. 25% reduction in preventable readmissions.',
      timeline: '8 weeks',
      link: '/case-studies/healthcare-ai'
    },
    {
      industry: 'Payments',
      challenge: 'Caribbean food delivery platform losing $3M+ annually to fraudulent orders',
      solution: 'Custom fraud detection model (XGBoost + Neural Net) with real-time scoring via SageMaker endpoints',
      result: '77.8% live prediction accuracy. $3M+ in prevented chargebacks. 80% reduction in manual reviews.',
      timeline: '6 weeks',
      link: '/case-studies/payment-automation'
    },
    {
      industry: 'Manufacturing',
      challenge: 'Demand planning done manually in Excel — 40% forecast error causing stockouts and waste',
      solution: 'Custom AI demand forecasting model with 50+ feature inputs, integrated with ERP for automatic PO generation',
      result: '50% better forecast accuracy. $2M annual savings in inventory costs.',
      timeline: '5 weeks',
      link: '/case-studies/manufacturing-ai'
    }
  ];

  const process = [
    { step: '01', title: 'Problem Definition', description: 'We work with you to define the exact AI problem, success metrics, and data requirements before writing a line of code', duration: '3–5 days' },
    { step: '02', title: 'Architecture Design', description: 'Technology selection, data pipeline design, model architecture, and integration plan — all documented before build starts', duration: '1 week' },
    { step: '03', title: 'Build, Train & Evaluate', description: 'Agile development with weekly demos. Model training, evaluation, and iteration until accuracy targets are met', duration: '3–8 weeks' },
    { step: '04', title: 'Deploy & Handover', description: 'Production deployment with monitoring, documentation, and optional ongoing support or team training', duration: '1–2 weeks' }
  ];

  const capabilities = [
    'Large Language Models (GPT-4, Claude, Llama)', 'Machine Learning (scikit-learn, XGBoost, LightGBM)',
    'Deep Learning (PyTorch, TensorFlow, Keras)', 'Computer Vision (YOLO, Detectron2, custom CNNs)',
    'NLP & Text Analytics', 'Time Series Forecasting', 'Reinforcement Learning',
    'Recommendation Systems', 'Anomaly Detection', 'RAG & Vector Search',
    'MLOps & Model Monitoring', 'Edge AI & Model Compression'
  ];

  const industries = [
    { name: 'Healthcare & Life Sciences', link: '/ai-consulting-healthcare' },
    { name: 'Insurance & Financial Services', link: '/ai-consulting-insurance' },
    { name: 'Manufacturing & Industry 4.0', link: '/solutions/artificial-intelligence' },
    { name: 'Retail & E-commerce', link: '/case-studies/retail-analytics' },
    { name: 'Logistics & Supply Chain', link: '/ai-consulting-logistics' },
    { name: 'FMCG & Consumer Goods', link: '/fmcg-ai' }
  ];

  return (
    <>
      <Helmet>
        <title>Custom AI Development Company | Bespoke AI Solutions Built in Weeks | Agility</title>
        <meta name="description" content="Custom AI development company building bespoke AI solutions in 4-10 weeks. Predictive models, AI automation, computer vision, LLM products, AI APIs. 200+ successful AI projects. Senior engineers only." />
        <meta name="keywords" content="custom AI development, bespoke AI solutions, AI software development company, AI application development, custom machine learning development, AI product development, AI development company, custom artificial intelligence, AI project development, AI engineering services" />
        <link rel="canonical" href="https://www.agilitytech.ai/custom-ai-development" />
        <meta property="og:title" content="Custom AI Development | Bespoke AI Solutions Built in Weeks | Agility" />
        <meta property="og:description" content="200+ custom AI projects delivered. Predictive models, LLM products, computer vision, AI automation. Senior engineers, fixed timelines, production-ready code." />
        <meta property="og:url" content="https://www.agilitytech.ai/custom-ai-development" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Custom AI Development",
            "description": "Bespoke AI software development — predictive models, LLM-powered products, computer vision systems, AI automation — delivered in production in 4-10 weeks.",
            "provider": { "@type": "Organization", "name": "Agility", "url": "https://www.agilitytech.ai" },
            "areaServed": "Worldwide",
            "serviceType": "Custom AI Development"
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-900 via-black to-teal-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(30deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6),linear-gradient(150deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6)]" style={{ backgroundSize: '80px 140px' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full mb-8">
            <Code className="w-4 h-4 text-teal-400" />
            <span className="text-teal-300 text-sm font-medium">Custom AI Development</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            AI Built <span className="text-teal-400">Exactly for You</span>
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
            No off-the-shelf tools. No generic solutions. We engineer custom AI systems —
            predictive models, LLM products, computer vision, automation — built to your exact
            requirements and delivered in 4–10 weeks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all">
              Discuss My AI Project <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/case-studies" className="group inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all">
              View Case Studies <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[['200+', 'AI Projects Delivered'],['4–10', 'Weeks to Production'],['99%', 'Client Satisfaction'],['0', 'Junior Devs on Your Project']].map(([stat, label]) => (
              <div key={label} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-2">{stat}</div>
                <div className="text-gray-400">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Build */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What We <span className="text-teal-600">Build</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From standalone ML models to full AI-powered products — built for production from day one
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whatWeBuild.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="fade-in-up bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border-2 border-gray-200 hover:border-teal-500 transition-all">
                  <Icon className="w-12 h-12 text-teal-600 mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600 mb-6">{item.description}</p>
                  <ul className="space-y-2">
                    {item.examples.map((ex, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-700">
                        <Zap className="w-4 h-4 text-teal-500 flex-shrink-0" /> {ex}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Custom AI <span className="text-teal-600">in Production</span>
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {caseStudies.map((study, i) => (
              <Link key={i} to={study.link} className="fade-in-up bg-gradient-to-br from-teal-50 to-white rounded-xl p-8 border border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all group">
                <div className="text-sm font-semibold text-teal-600 mb-4">{study.industry}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Challenge</h3>
                <p className="text-gray-700 mb-4">{study.challenge}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Solution</h3>
                <p className="text-gray-700 mb-4">{study.solution}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Results</h3>
                <p className="text-gray-700 mb-6">{study.result}</p>
                <div className="flex items-center justify-between pt-6 border-t border-teal-200">
                  <div>
                    <div className="text-sm text-gray-500">Timeline</div>
                    <div className="font-semibold text-gray-900">{study.timeline} to production</div>
                  </div>
                  <div className="flex items-center gap-1 text-teal-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    Read more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Full AI <span className="text-teal-400">Capability Stack</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">We cover the full breadth of modern AI/ML — not just LLMs</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-5xl mx-auto">
            {capabilities.map((cap, i) => (
              <span key={i} className="px-4 py-2 bg-gray-800 text-gray-200 rounded-full text-sm border border-gray-700 hover:border-teal-500 transition-colors">{cap}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How We Build <span className="text-teal-600">Your AI</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((p, i) => (
              <div key={i} className="fade-in-up text-center">
                <div className="w-20 h-20 bg-teal-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">{p.step}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{p.title}</h3>
                <p className="text-gray-600 mb-4">{p.description}</p>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">
                  <Clock className="w-4 h-4" /> {p.duration}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Industries We <span className="text-teal-600">Serve</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((ind, i) => (
              <Link key={i} to={ind.link} className="fade-in-up flex items-center justify-between bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-400 hover:shadow-md transition-all group">
                <span className="font-semibold text-gray-900 group-hover:text-teal-700 transition-colors">{ind.name}</span>
                <ArrowRight className="w-5 h-5 text-teal-500 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-600 to-teal-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Have an AI Problem? Let's Solve It.</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            No discovery calls that go nowhere. We'll review your use case, tell you if it's viable, scope it, and start building.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-lg font-bold text-lg transition-all">
              Tell Us Your AI Challenge <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/ai-implementation-services" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold text-lg transition-all">
              View All AI Services
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default CustomAIDevelopment;
